import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Alert,
  App,
  Button,
  Flex,
  Modal,
  Skeleton,
  theme,
  Typography
} from 'antd'
import { Paperclip, Smile } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  supportTicketsCommentsCreateMutation,
  supportTicketsCommentsListOptions,
  supportTicketsCommentsListQueryKey,
  supportTicketsListQueryKey,
  supportTicketsRetrieveOptions,
  supportTicketsUnseenCountRetrieveQueryKey,
  type SupportTicket,
  type SupportTicketAttachment,
  type SupportTicketComment,
  type UserShortWithSettings
} from 'src/api/main'
import { AvatarWithInitials, DateTimeCell, EmojiPicker } from 'src/components'
import { type ChatComposerHandle, ChatComposer } from 'src/components/chat'
import { useInvalidatePredicateQueries, useSessionContext } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'
import { composeDisplayName } from 'src/utils/string'

import { PriorityLabel } from '../PriorityLabel'
import { StatusBadge } from '../StatusBadge'

import { useStyles } from './styles'

type Props = {
  ticket: SupportTicket
  onClose: () => void
}

const formatFileSize = (size: number | null): string | null => {
  if (!size || size <= 0) return null
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`

  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const AttachmentChip = ({
  attachment
}: {
  attachment: SupportTicketAttachment
}) => {
  const { styles } = useStyles()
  const size = formatFileSize(attachment.size)

  return (
    <a
      className={styles.attachmentChip}
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      title={attachment.name}
    >
      <Paperclip size={14} />
      <span className={styles.attachmentName}>{attachment.name}</span>
      {size ? <span className={styles.muted}>{size}</span> : null}
    </a>
  )
}

const ActivityMessage = ({
  comment,
  clientUser
}: {
  comment: SupportTicketComment
  clientUser?: UserShortWithSettings
}) => {
  const { styles, cx } = useStyles()
  const { token } = theme.useToken()
  const { t } = useTranslation()
  const isSupport = comment.source === 'clickup'
  const supportName = t('tickets.commentFromClickup')
  const clientName = composeDisplayName(
    clientUser?.first_name,
    clientUser?.last_name,
    clientUser?.email
  )
  const timestamp = (
    <DateTimeCell
      className={styles.muted}
      value={comment.created_at}
      variant="dateTime"
    />
  )

  return (
    <Flex vertical gap={2}>
      {isSupport ? (
        <Flex align="center" gap="small">
          <span aria-hidden className={cx(styles.avatar, styles.avatarSupport)}>
            {supportName.slice(0, 1)}
          </span>
          <span className={styles.messageAuthor}>{supportName}</span>
          {timestamp}
        </Flex>
      ) : (
        <Flex align="center" gap="small">
          <AvatarWithInitials
            name={clientName}
            id={clientUser?.id}
            shape="square"
            size={token.controlHeight}
            src={clientUser?.photo?.file}
          />
          <span className={styles.messageAuthor}>{clientName}</span>
          {timestamp}
        </Flex>
      )}
      <div
        className={cx(styles.bubble, isSupport && styles.bubbleSupport)}
        style={{ marginInlineStart: token.controlHeight + token.marginXS }}
      >
        {comment.text}
        {comment.images.map((image) => (
          <a
            key={`${comment.id}-${image.url}`}
            href={image.url}
            target="_blank"
            rel="noreferrer"
          >
            <img
              className={styles.messageImage}
              src={image.url}
              alt={image.name ?? t('tickets.attachments')}
            />
          </a>
        ))}
      </div>
    </Flex>
  )
}

export const TicketConversation = ({ ticket, onClose }: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { user } = useSessionContext()
  const threadRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<ChatComposerHandle>(null)
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const showApiErrors = useShowApiErrors()
  const [commentText, setCommentText] = useState('')

  const commentsQuery = useQuery({
    ...supportTicketsCommentsListOptions({ path: { ticket_id: ticket.id } })
  })

  const detailsQuery = useQuery({
    ...supportTicketsRetrieveOptions({ path: { ticket_id: ticket.id } })
  })

  const details = detailsQuery.data ?? ticket
  const comments = commentsQuery.data ?? []

  const { mutate: addComment, isPending: isSending } = useMutation({
    ...supportTicketsCommentsCreateMutation(),
    onSuccess: (_data, variables) => {
      invalidateQueries([
        supportTicketsCommentsListQueryKey({ path: variables.path })
      ])
      setCommentText('')
      message.success(t('tickets.commentAdded'))
    },
    onError: (error) => showApiErrors(error)
  })

  useEffect(() => {
    if (!commentsQuery.isSuccess) return

    invalidateQueries([
      supportTicketsUnseenCountRetrieveQueryKey(),
      supportTicketsListQueryKey()
    ])
  }, [commentsQuery.isSuccess, invalidateQueries])

  useEffect(() => {
    const thread = threadRef.current
    if (!thread) return

    thread.scrollTop = thread.scrollHeight
  }, [comments])

  useEffect(() => {
    const frame = requestAnimationFrame(() => composerRef.current?.focus())

    return () => cancelAnimationFrame(frame)
  }, [])

  const onSend = () => {
    const trimmed = commentText.trim()
    if (!trimmed || details.completed) return

    addComment({ path: { ticket_id: ticket.id }, body: { text: trimmed } })
  }

  const onEmojiSelect = (emoji: string) => {
    setCommentText((current) => `${current}${emoji}`)
    composerRef.current?.focus()
  }

  return (
    <Modal open onCancel={onClose} footer={null} width={730} centered>
      <Flex vertical gap="small" className={styles.header}>
        <Flex align="center" wrap gap="small">
          <Typography.Title level={3} className={styles.title}>
            {details.title}
          </Typography.Title>
          <StatusBadge ticket={details} className={styles.statusPill} />
        </Flex>

        <Flex align="center" wrap gap="small" className={styles.meta}>
          <span>{t('tickets.priority.label')}</span>
          <span className={styles.metaValue}>
            <PriorityLabel priority={details.priority} />
          </span>
          <span>·</span>
          <span>{t('tickets.opened')}</span>
          <span className={styles.metaValue}>
            <DateTimeCell value={details.created_at} variant="date" />
          </span>
        </Flex>
      </Flex>

      <Flex vertical gap="middle" className={styles.section}>
        {detailsQuery.isError ? (
          <Alert
            type="warning"
            showIcon
            title={t('tickets.detailsLoadError')}
          />
        ) : null}

        {detailsQuery.isLoading ? (
          <Skeleton active paragraph={{ rows: 2 }} title={false} />
        ) : (
          <>
            {details.description.trim() ? (
              <Flex vertical gap="small">
                <span className={styles.muted}>
                  {t('tickets.originalRequest')}
                </span>
                <p className={styles.description}>
                  {details.description.trim()}
                </p>
              </Flex>
            ) : null}

            {details.attachments.length > 0 ? (
              <Flex wrap gap="small">
                {details.attachments.map((attachment) => (
                  <AttachmentChip key={attachment.id} attachment={attachment} />
                ))}
              </Flex>
            ) : null}
          </>
        )}
      </Flex>

      <Flex vertical gap="middle" className={styles.section}>
        <Flex align="center" gap="small" className={styles.muted}>
          {t('tickets.activity')}
          <span className={styles.activityCount}>{comments.length}</span>
        </Flex>

        {commentsQuery.isError ? (
          <Alert type="error" showIcon title={t('tickets.commentsLoadError')} />
        ) : commentsQuery.isLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        ) : comments.length ? (
          <Flex vertical gap="middle" ref={threadRef} className={styles.thread}>
            {comments.map((comment) => (
              <ActivityMessage
                key={comment.id}
                comment={comment}
                clientUser={user?.user}
              />
            ))}
          </Flex>
        ) : (
          <span className={styles.muted}>{t('tickets.noComments')}</span>
        )}
      </Flex>

      {details.completed ? (
        <Alert type="info" showIcon title={t('tickets.closedChatReadOnly')} />
      ) : (
        <ChatComposer
          ref={composerRef}
          className={styles.composer}
          value={commentText}
          onChange={setCommentText}
          onSend={onSend}
          onStop={() => undefined}
          streaming={false}
          disabled={isSending}
          placeholder={t('tickets.commentPlaceholder')}
          prefix={
            <EmojiPicker onSelect={onEmojiSelect}>
              <Button
                type="text"
                size="small"
                disabled={isSending}
                aria-label={t('tickets.emoji')}
                icon={<Smile size={18} />}
              />
            </EmojiPicker>
          }
        />
      )}
    </Modal>
  )
}
